import React, { useState, useEffect } from "react";
import { Download, Upload, PlusCircle, Send, X, CheckCircle, Save, FolderOpen, Menu, Search } from "lucide-react";
import Swal from 'sweetalert2';

// Since we can't import from external files in artifacts, using inline config
import API_BASE_URL from "../config";

export default function PurchaseRequisitionUI() {
  const [activeTab, setActiveTab] = useState("info");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(() => {
    const user = JSON.parse(localStorage.getItem("user"));

    // ✅ COMBINE FirstName + LastName
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`.toUpperCase();
    }

    return user?.username || "Unknown User";
  });
  // Item Search Modal State
  const [showItemModal, setShowItemModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [itemsList, setItemsList] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [itemSearchQuery, setItemSearchQuery] = useState("");

  // ✅ PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50; // Show 50 items per page

  const [headerData, setHeaderData] = useState({
    requestDate: new Date().toISOString().split('T')[0],
    dateNeeded: "",
    department: "",
    type: "",
    requestor: "",
    typeOfPurchase: "Item",
    remarks: "",
    inventoryDestinationId: "",
    priority: ""
  });
  const [inventoryDestinations, setInventoryDestinations] = useState([]);
  const [controlNumber, setControlNumber] = useState("");

  // ✅ Fetch Control Number on component mount
  useEffect(() => {
    const fetchControlNumber = async () => {
      const res = await fetch(`${API_BASE_URL}api/PR_Header/GenerateControlNumber`);
      const ctn = await res.text();
      setControlNumber(ctn);
    };

    fetchControlNumber();
  }, []);
  useEffect(() => {
    fetch(`${API_BASE_URL}api/InventoryDestination`)
      .then(res => res.json())
      .then(data => setInventoryDestinations(data))
      .catch(err => console.error("Error loading inventory destinations:", err));
  }, []);

  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/M_Payment_Method`)
      .then(res => res.json())
      .then(data => setPaymentMethods(data))
      .catch(err => console.error("Error loading payment methods:", err));
  }, []);

  const [expenseTypes, setExpenseTypes] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/ExpenseType`)
      .then(res => res.json())
      .then(data => setExpenseTypes(data))
      .catch(err => console.error("Error loading expense types:", err));
  }, []);

  const [shippingInfo, setShippingInfo] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/ShippingInfo`)
      .then(res => res.json())
      .then(data => setShippingInfo(data))
      .catch(err => console.error("Error fetching shipping info:", err));
  }, []);

  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/Company`)
      .then(res => res.json())
      .then(data => setCompanies(data))
      .catch(err => console.error("Error fetching company list:", err));
  }, []);

  const [costCenters, setCostCenters] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/Location`)
      .then(res => res.json())
      .then(data => setCostCenters(data))
      .catch(err => console.error("Failed to load locations", err));
  }, []);

  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/Department`)
      .then(res => res.json())
      .then(data => setDepartments(data))
      .catch(err => console.error("Department fetch error:", err));
  }, []);

  // Fetch Items from API
  useEffect(() => {
    fetch(`${API_BASE_URL}api/Items`)
      .then(res => res.json())
      .then(data => {
        setItemsList(data);
        setFilteredItems(data);
      })
      .catch(err => console.error("Error fetching items:", err));
  }, []);
  // ✅ EXPORT TEMPLATE (Empty CSV with headers only)
  const exportTemplate = () => {
    const headers = ["Item Description", "Specification", "Qty", "UOM", "Remarks"];
    const csvContent = headers.join(",");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "PR_Items_Template.csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      icon: 'success',
      title: 'Template Downloaded',
      text: 'Fill out the template and import it back!',
      timer: 2000,
      showConfirmButton: false
    });
  };

  // ✅ EXPORT CURRENT ITEMS (With data)
  const exportItems = () => {
    if (items.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Items',
        text: 'Add items first before exporting.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const headers = ["ItemCode", "Item Description", "Specification", "Qty", "UOM", "Remarks"];
    const rows = items.map(item => [
      item.itemDescription || "",
      item.material || "",
      item.qty || 0,
      item.uom || "",
      item.remarks || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute("href", url);
    link.setAttribute("download", `PR_Items_${prCode}_${timestamp}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      icon: 'success',
      title: 'Items Exported',
      text: `${items.length} items exported successfully!`,
      timer: 2000,
      showConfirmButton: false
    });
  };

  // ✅ IMPORT ITEMS FROM CSV
  const handleImportItems = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const rows = text.split("\n").filter(row => row.trim());

        // Skip header row
        const dataRows = rows.slice(1);

        if (dataRows.length === 0) {
          await Swal.fire({
            icon: 'warning',
            title: 'Empty File',
            text: 'The CSV file has no data rows.',
            confirmButtonText: 'OK'
          });
          return;
        }

        const importedItems = dataRows.map(row => {
          // Parse CSV row (handle quotes)
          const columns = row.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [];
          const cleanColumns = columns.map(col => col.replace(/^"|"$/g, '').trim());

          return {
            itemDescription: cleanColumns[0] || "",
            material: cleanColumns[1] || "",
            qty: parseFloat(cleanColumns[2]) || 0,
            uom: cleanColumns[3] || "",
            remarks: cleanColumns[4] || ""
          };
        });

        // Ask user if they want to replace or append
        const result = await Swal.fire({
          title: 'Import Items',
          text: `Found ${importedItems.length} items. What would you like to do?`,
          icon: 'question',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Replace All',
          denyButtonText: 'Append to Existing',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#d33',
          denyButtonColor: '#3085d6'
        });

        if (result.isConfirmed) {
          // Replace all items
          setItems(importedItems);
          await Swal.fire({
            icon: 'success',
            title: 'Items Replaced',
            text: `${importedItems.length} items imported successfully!`,
            timer: 2000,
            showConfirmButton: false
          });
        } else if (result.isDenied) {
          // Append to existing
          setItems([...items, ...importedItems]);
          await Swal.fire({
            icon: 'success',
            title: 'Items Added',
            text: `${importedItems.length} items appended successfully!`,
            timer: 2000,
            showConfirmButton: false
          });
        }

        // Reset file input
        event.target.value = '';

      } catch (error) {
        console.error("Import error:", error);
        await Swal.fire({
          icon: 'error',
          title: 'Import Failed',
          text: 'Failed to parse CSV file. Please check the format.',
          confirmButtonText: 'OK'
        });
      }
    };

    reader.readAsText(file);
  };
  // Items state
  const [items, setItems] = useState([
    { qty: "", uom: "", itemDescription: "", material: "", remarks: "" },
  ]);

  // Attachment state
  const [attachment, setAttachment] = useState({
    file: null,
    description: ""
  });

  const handleHeaderChange = (field, value) => {
    setHeaderData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (field === "qty" || field === "unitPrice") {
      const qty = parseFloat(newItems[index].qty) || 0;
      const unitPrice = parseFloat(newItems[index].unitPrice) || 0;
      newItems[index].totalBudget = (qty * unitPrice).toString();
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, {
      qty: "",
      uom: "",
      itemDescription: "",
      material: "",
      remarks: ""  // ✅ I-update mo dito
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };


  const [requestors, setRequestors] = useState([]);
  const [showRequestorModal, setShowRequestorModal] = useState(false);
  const [filteredRequestors, setFilteredRequestors] = useState([]);
  const [requestorSearchQuery, setRequestorSearchQuery] = useState("");
  useEffect(() => {
    fetch(`${API_BASE_URL}api/Requestor`)
      .then(res => res.json())
      .then(data => {
        setRequestors(data);
        setFilteredRequestors(data); // 👈 Initialize filtered list
      })
      .catch(err => console.error("Error loading requestors:", err));
  }, []);
  const handleRequestorSearch = (query) => {
    setRequestorSearchQuery(query);
    if (query.trim() === "") {
      setFilteredRequestors(requestors);
    } else {
      const filtered = requestors.filter(req =>
        req.name?.toLowerCase().includes(query.toLowerCase()) ||
        req.department?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredRequestors(filtered);
    }
  };

  const selectRequestor = (requestor) => {
    handleHeaderChange("requestor", requestor.name); // 👈 Name para sa display
    handleHeaderChange("requestorId", requestor.id);  // 👈 ID para sa database
    handleHeaderChange("department", requestor.department); // 👈 ADD THIS LINE
    setShowRequestorModal(false);
    setRequestorSearchQuery("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setAttachment(prev => ({ ...prev, file }));
  };

  // Open Item Search Modal
  const openItemSearchModal = (index) => {
    setCurrentItemIndex(index);
    setShowItemModal(true);
    setItemSearchQuery("");
    setFilteredItems(itemsList);
    setCurrentPage(1); // ✅ Reset to page 1
  };

  // Search Items
  const handleItemSearch = (query) => {
    setItemSearchQuery(query);
    setCurrentPage(1); // ✅ Reset to page 1 on search

    if (query.trim() === "") {
      setFilteredItems(itemsList);
    } else {
      const filtered = itemsList.filter(item =>
        item.itemDesciption?.toLowerCase().includes(query.toLowerCase()) ||
        item.itemCode?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  };

  // ✅ UPDATED: Select Item from Modal - Auto-fill UOM from oumSystem
  const selectItem = (item) => {
    if (currentItemIndex !== null) {
      // ✅ Auto-fill Item Code
      if (item.itemCode) {
        handleItemChange(currentItemIndex, "itemCode", item.itemCode);
      }

      // ✅ Auto-fill Item Description
      handleItemChange(currentItemIndex, "itemDescription", item.itemDesciption);

      // ✅ Auto-fill UOM from item's oumSystem (keep original case, display kahit wala sa options)
      if (item.oumSystem) {
        handleItemChange(currentItemIndex, "uom", item.oumSystem);
      }

      setShowItemModal(false);
      setCurrentItemIndex(null);
      setItemSearchQuery("");
    }
  };
  const logPRCreate = async (prCode, prHeaderId, userId, itemsPosted) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "CREATE",
          module: "Purchase Request",
          description: `PR ${prCode} created with ${itemsPosted} item(s). PR ID: ${prHeaderId}`,
          userId: user?.userId || userId,
          username: user?.username || "unknown",
          fullName: user?.fullName || user?.username || "unknown",
        })
      });
    } catch (err) {
      console.error("Failed to log PR creation:", err); // silent fail
    }
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    const missingFields = [];

    // ===== HEADER VALIDATION =====
    if (!headerData.dateNeeded) missingFields.push("Date Needed");
    if (!headerData.priority) missingFields.push("Priority");
    if (!headerData.type) missingFields.push("Type");
    if (!headerData.requestor) missingFields.push("Requestor");

    // ===== ITEM VALIDATION =====
    const hasValidItem = items.some(
      (item) => item.qty && parseFloat(item.qty) > 0
    );

    if (!hasValidItem) {
      missingFields.push("At least one item with Quantity");
    }

    if (missingFields.length > 0) {
      // ✅ SWAL ERROR
      Swal.fire({
        icon: 'error',
        title: 'Missing Required Fields',
        html: `Please fill in:<br/><strong>${missingFields.join("<br/>")}</strong>`,
        confirmButtonColor: '#dc2626'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.userId || 1;

      // ✅ FIX: Handle inventoryDestinationId properly
      let inventoryDestId = null;

      if (headerData.inventoryDestinationId) {
        inventoryDestId = parseInt(headerData.inventoryDestinationId);
      } else {
        // ✅ Check if only one active destination exists
        const activeDests = inventoryDestinations.filter(dest => dest.isActive);
        if (activeDests.length === 1) {
          inventoryDestId = activeDests[0].destinationId;
        }
      }

      console.log("📦 Inventory Destination ID:", inventoryDestId);

      // ===== HEADER PAYLOAD =====
      const headerPayload = {
        prNo: prCode || "AUTO",
        inventoryDestinationId: inventoryDestId,
        creationDate: new Date(headerData.requestDate).toISOString(),
        dateNeeded: new Date(headerData.dateNeeded).toISOString(),
        createdBy: userId,
        requestor: headerData.requestor,
        department: headerData.department,
        remarks: headerData.remarks || "",
        status: 0,
        dateSubmittedForApproval: new Date().toISOString(),
        approverLevel: 0,
        notification: false,
        seen_Status: false,
        priority: headerData.priority || "",
        type: headerData.type || "",
        typeOfPurchase: headerData.typeOfPurchase || "Item",
        controlNumber: controlNumber
      };

      console.log("📤 Sending PR Header:", headerPayload);

      const headerResponse = await fetch(
        `${API_BASE_URL}api/PR_Header`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(headerPayload)
        }
      );

      if (!headerResponse.ok) {
        const errorText = await headerResponse.text();
        console.error("❌ API Error:", errorText);
        throw new Error(`Failed to create PR Header: ${errorText}`);
      }

      const headerResult = await headerResponse.json();
      const prHeaderId = headerResult.id;

      console.log("✅ PR Header Created! ID:", prHeaderId);

      // ===== FILTER ITEMS =====
      const validItems = items.filter(
        (item) => item.qty && parseFloat(item.qty) > 0
      );

      let itemsPosted = 0;

      for (const item of validItems) {
        const detailPayload = {
          PRHeaderId: prHeaderId,
          Qty: parseFloat(item.qty) || 0,
          UOM: item.uom || "",
          ItemCode: item.itemCode || "",  // ✅ DAGDAG: Item Code
          ItemDescription: item.itemDescription || "",
          MaterialFinish: item.material || "",
          Purpose: "",
          Remarks: item.remarks || ""
        };

        console.log("📤 Posting item:", detailPayload);

        const detailResponse = await fetch(
          `${API_BASE_URL}api/PR_Details`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(detailPayload)
          }
        );

        if (!detailResponse.ok) {
          const errorText = await detailResponse.text();
          console.error("❌ Item Post Error:", errorText);
          throw new Error(`Failed to post item (Qty: ${item.qty}): ${errorText}`);
        }

        itemsPosted++;
      }

      console.log(`✅ Posted ${itemsPosted} items`);
      await logPRCreate(prCode, prHeaderId, userId, itemsPosted);

      // ===== ATTACHMENT =====
      let attachmentUploaded = false;

      if (attachment.file) {
        const formData = new FormData();
        formData.append("file", attachment.file);
        formData.append("PRHeaderID", prHeaderId);
        formData.append("Description", attachment.description?.trim() || "Attachment");

        console.log("📤 Uploading attachment...");

        const attachmentResponse = await fetch(
          `${API_BASE_URL}api/PR_Attachments/upload`,
          {
            method: "POST",
            body: formData
          }
        );

        if (!attachmentResponse.ok) {
          const errorText = await attachmentResponse.text();
          console.error("❌ Attachment Upload Error:", errorText);
          throw new Error(`Failed to upload attachment: ${errorText}`);
        }

        attachmentUploaded = true;
        console.log("✅ Attachment uploaded");
      }

      // ✅ SWAL SUCCESS
      Swal.fire({
        icon: 'success',
        title: 'PR Submitted!',
        html: `
        <strong>${prCode}</strong> successfully submitted!<br/>
        <span class="text-sm text-gray-600">
          ${itemsPosted} item(s)${attachmentUploaded ? " + attachment" : ""}
        </span>
      `,
        confirmButtonColor: '#16a34a',
        timer: 3000,
        timerProgressBar: true
      }).then(() => {
        window.location.reload();
      });

    } catch (error) {
      console.error("❌ Submission Error:", error);
      // ✅ SWAL ERROR
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: error.message || "Failed to submit PR",
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const [prCode, setPrCode] = useState();
  const [savedDrafts, setSavedDrafts] = useState(() => {
    const stored = localStorage.getItem("pr_drafts");
    return stored ? JSON.parse(stored) : [];
  });
  const [showDraftModal, setShowDraftModal] = useState(false);

  useEffect(() => {
    const fetchPRCode = async () => {
      const res = await fetch(`${API_BASE_URL}api/PR_Header/GenerateNextPRNo`);
      const code = await res.text();
      setPrCode(code);
    };

    fetchPRCode();
  }, []);

  const saveDraft = () => {
    const draft = {
      id: Date.now(),
      prCode: prCode,
      headerData: headerData,
      items: items,
      attachment: { description: attachment.description },
      savedAt: new Date().toISOString()
    };

    const newDrafts = [...savedDrafts, draft];

    setSavedDrafts(newDrafts);
    localStorage.setItem("pr_drafts", JSON.stringify(newDrafts));

    Swal.fire({
      icon: 'success',
      title: 'Draft Saved',
      text: `Draft saved successfully! (PR: ${prCode})`,
      timer: 2000,
      showConfirmButton: false
    });
  };

  const loadDraft = (draft) => {
    setHeaderData(draft.headerData);
    setItems(draft.items);
    setAttachment({ file: null, description: draft.attachment.description });
    setPrCode(draft.prCode);
    setShowDraftModal(false);

    Swal.fire({
      icon: 'success',
      title: 'Draft Loaded',
      text: `Draft loaded successfully! (PR: ${draft.prCode})`,
      timer: 2000,
      showConfirmButton: false
    });
  };

  const deleteDraft = (draftId) => {
    const updated = savedDrafts.filter(d => d.id !== draftId);
    setSavedDrafts(updated);
    localStorage.setItem("pr_drafts", JSON.stringify(updated));
  };

  return (
    <div className="min-h-screen to-blue-50 p-8">
      <div className="w-full">
        {/* HEADER - Responsive */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-2xl p-8 mb-2">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white flex items-center gap-3">
                Purchase Requisition
              </h1>
              <p className="text-blue-100 text-xs sm:text-sm mt-1">
                Create and manage procurement requests
              </p>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <h2 className="px-4 sm:px-6 lg:px-10 py-2 sm:py-3 bg-white/20 backdrop-blur-md text-white 
        font-semibold text-sm sm:text-base lg:text-lg rounded-xl sm:rounded-2xl shadow-lg
        border border-white/30 tracking-wider text-center">
                {prCode}
              </h2>
              <p className="px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white text-xs font-mono rounded-lg">
                {controlNumber}
              </p>
            </div>
          </div>
        </div>


        {/* Item Search Modal */}
        {showItemModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[80vh] overflow-hidden">
              {/* ✅ CHANGED: max-w-3xl → max-w-5xl */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 sm:px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                  <Search size={20} />
                  Search Items
                </h3>
                <button
                  onClick={() => {
                    setShowItemModal(false);
                    setCurrentItemIndex(null);
                    setItemSearchQuery("");
                  }}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all">
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 sm:p-6">
                {/* Search Input */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search by item description or code..."
                      value={itemSearchQuery}
                      onChange={(e) => handleItemSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Items List */}
                <div className="overflow-y-auto max-h-[50vh] border border-slate-200 rounded-lg">
                  {filteredItems.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Search size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-semibold">No items found</p>
                      <p className="text-sm">Try a different search term</p>
                    </div>
                  ) : (
                    <>
                      <table className="w-full">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Item Code</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">OUM System</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">OUM Actual</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Supplier</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* ✅ PAGINATION: Show only current page items */}
                          {filteredItems
                            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                            .map((item) => (
                              <tr
                                key={item.id}
                                onClick={() => selectItem(item)}
                                className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors">
                                <td className="px-4 py-3 text-sm font-medium text-slate-800">{item.itemCode}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{item.itemDesciption}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                    {item.category || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                    {item.oumSystem || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                                    {item.oumActual || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">{item.supplierAddress}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>

                      {/* ✅ PAGINATION CONTROLS */}
                      <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between">
                        <div className="text-sm text-slate-600">
                          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} items
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                            Previous
                          </button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, Math.ceil(filteredItems.length / itemsPerPage)) }, (_, i) => {
                              const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
                              let pageNum;

                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`w-8 h-8 rounded text-sm font-medium ${currentPage === pageNum
                                    ? 'bg-blue-600 text-white'
                                    : 'border border-slate-300 hover:bg-slate-50'
                                    }`}>
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredItems.length / itemsPerPage), p + 1))}
                            disabled={currentPage >= Math.ceil(filteredItems.length / itemsPerPage)}
                            className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                            Next
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-4 text-xs text-slate-500 text-center">
                  {filteredItems.length} item(s) found
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Draft Modal */}
        {showDraftModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 sm:px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold">Saved Drafts</h3>
                <button
                  onClick={() => setShowDraftModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all">
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
                {savedDrafts.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FolderOpen size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-semibold">No saved drafts</p>
                    <p className="text-sm">Save your work as draft to access it later</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {savedDrafts.map((draft) => (
                      <div key={draft.id} className="border border-slate-200 rounded-lg p-3 sm:p-4 hover:bg-slate-50 transition-all">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                          <div className="flex-1 w-full">
                            <h4 className="font-bold text-slate-800 text-sm sm:text-base">{draft.prCode}</h4>
                            <p className="text-xs sm:text-sm text-slate-600 mt-1">
                              {draft.items.filter(i => i.itemDescription).length} items •
                              Saved {new Date(draft.savedAt).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Company: {draft.headerData.requestingCompany || 'Not set'}
                            </p>
                          </div>
                          <div className="flex gap-2 w-full sm:w-auto">
                            <button
                              onClick={() => loadDraft(draft)}
                              className="flex-1 sm:flex-initial px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-semibold">
                              Load
                            </button>
                            <button
                              onClick={() => deleteDraft(draft.id)}
                              className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all">
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Status Message */}
        {submitStatus && (
          <div className={`mx-4 sm:mx-6 lg:mx-8 mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg flex items-start gap-3 text-sm sm:text-base ${submitStatus.type === "success"
            ? "bg-green-50 border border-green-200 text-green-800"
            : "bg-red-50 border border-red-200 text-red-800"
            }`}>
            {submitStatus.type === "success" ? <CheckCircle size={20} className="flex-shrink-0 mt-0.5" /> : <X size={20} className="flex-shrink-0 mt-0.5" />}
            <span className="font-semibold">{submitStatus.message}</span>
          </div>
        )}

        {/* TAB BUTTONS - Responsive */}
        <div className="flex border-b bg-white px-4 sm:px-6 lg:px-8 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            <button
              onClick={() => setActiveTab("info")}
              className={`px-3 sm:px-4 lg:px-6 py-3 sm:py-4 font-semibold transition-all duration-200 relative text-xs sm:text-sm lg:text-base whitespace-nowrap ${activeTab === "info" ? "text-blue-700" : "text-slate-500 hover:text-blue-600"
                }`}>
              PR Information
              {activeTab === "info" && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"></div>
              )}
            </button>

            <span className="flex items-center text-slate-300 font-bold text-xs sm:text-sm">/</span>

            <button
              onClick={() => setActiveTab("items")}
              className={`px-3 sm:px-4 lg:px-6 py-3 sm:py-4 font-semibold transition-all duration-200 relative text-xs sm:text-sm lg:text-base whitespace-nowrap ${activeTab === "items" ? "text-blue-700" : "text-slate-500 hover:text-blue-600"
                }`}>
              Items Requested
              {activeTab === "items" && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"></div>
              )}
            </button>

            <span className="flex items-center text-slate-300 font-bold text-xs sm:text-sm">/</span>

            <button
              onClick={() => setActiveTab("attachments")}
              className={`px-3 sm:px-4 lg:px-6 py-3 sm:py-4 font-semibold transition-all duration-200 relative text-xs sm:text-sm lg:text-base whitespace-nowrap ${activeTab === "attachments" ? "text-blue-700" : "text-slate-500 hover:text-blue-600"
                }`}>
              Attachments
              {activeTab === "attachments" && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"></div>
              )}
            </button>
          </div>
        </div>

        {/* TAB CONTENTS */}
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">

          {/* TAB 1: PURCHASE REQUEST INFORMATION */}
          {activeTab === "info" && (
            <div className="backdrop-blur-md rounded-xl shadow-lg border border-white/25 p-4 sm:p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="w-1 h-6 sm:h-8 bg-blue-600 rounded-full"></div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800">
                  Purchase Request Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 text-sm">
                <div>
                  <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">
                    Control Number:
                  </label>
                  <input
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                    value={controlNumber}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">
                    Request Date:
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-200 rouInventory Destination:nded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm cursor-not-allowed opacity-60"
                      type="date"
                      value={headerData.requestDate}
                      onChange={(e) => handleHeaderChange("requestDate", e.target.value)}
                      defaultValue={new Date().toISOString().split('T')[0]}
                      disabled
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">
                    Date Needed: <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${!headerData.dateNeeded && submitStatus?.type === "error"
                        ? "border-red-300 bg-red-50"
                        : "border-slate-200 bg-white"
                        }`}
                      type="date"
                      value={headerData.dateNeeded}
                      onChange={(e) => handleHeaderChange("dateNeeded", e.target.value)}
                    />
                  </div>
                </div>



                <div>
                  <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">
                    Type of Purchase:
                  </label>
                  <select
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm cursor-pointer"
                    value={headerData.typeOfPurchase || "Item"}
                    onChange={(e) => handleHeaderChange("typeOfPurchase", e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="Item">Item</option>
                    <option value="Service">Service</option>
                  </select>
                </div>



                <div>
                  <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">
                    Created By:
                  </label>
                  <input
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={loggedInUser}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">
                    Priority: <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${!headerData.priority && submitStatus?.type === "error"
                      ? "border-red-300 bg-red-50"
                      : "border-slate-200"
                      }`}
                    value={headerData.priority}
                    onChange={(e) => handleHeaderChange("priority", e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Emergency Order">Emergency Order</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>



                <div>
                  <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">
                    Inventory Destination:
                  </label>
                  <div className="relative">
                    <select
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-20 border border-slate-200 rounded-lg cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none"
                      value={headerData.inventoryDestinationId || (inventoryDestinations.filter(dest => dest.isActive).length === 1 ? inventoryDestinations.filter(dest => dest.isActive)[0].destinationId : "")}
                      onChange={(e) => handleHeaderChange("inventoryDestinationId", e.target.value)}>
                      <option value="">Select...</option>
                      {inventoryDestinations.filter(dest => dest.isActive).map((dest) => (
                        <option key={dest.destinationId} value={dest.destinationId}>
                          {dest.destinationCode} - {dest.destinationName}
                        </option>
                      ))}
                    </select>

                    {(() => {
                      const currentId = headerData.inventoryDestinationId ||
                        (inventoryDestinations.filter(dest => dest.isActive).length === 1
                          ? inventoryDestinations.filter(dest => dest.isActive)[0].destinationId
                          : null);

                      if (!currentId) return null;

                      const selected = inventoryDestinations.find(d => d.destinationId == currentId);
                      if (!selected) return null;

                      const typeConfig = selected.wms
                        ? { label: 'WMS', color: 'bg-blue-600' }
                        : selected.asset
                          ? { label: 'ASSET', color: 'bg-purple-600' }
                          : { label: 'BOTH', color: 'bg-amber-600' };

                      return (
                        <span className={`absolute right-10 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded text-xs font-bold text-white ${typeConfig.color}`}>
                          {typeConfig.label}
                        </span>
                      );
                    })()}

                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block font-semibold mb-2  bt-2 text-slate-600 text-xs sm:text-sm">
                    Type: <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${!headerData.type && submitStatus?.type === "error"
                      ? "border-red-300 bg-red-50"
                      : "border-slate-200"
                      }`}
                    value={headerData.type}
                    onChange={(e) => handleHeaderChange("type", e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="Local">Local</option>
                    <option value="Import">Import</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Requestor: *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={headerData.requestor || ""}  // 👈 Display yung NAME
                      placeholder="Click to search requestor..."
                      readOnly
                      className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white"
                      onClick={() => setShowRequestorModal(true)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowRequestorModal(true)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1.5 transition-all"
                      title="Search requestor"
                    >
                      <Search size={20} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Department:
                  </label>
                  <input
                    type="text"
                    value={headerData.department || ""}
                    readOnly
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-slate-50 text-slate-700 cursor-not-allowed"
                    placeholder="Auto-filled from requestor"
                  />
                </div>
              </div>

              <div className="mt-4 sm:mt-6">
                <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">Remarks / Notes:</label>
                <textarea
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-200 rounded-lg h-24 sm:h-32 resize-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  value={headerData.remarks}
                  onChange={(e) => handleHeaderChange("remarks", e.target.value)}
                ></textarea>
              </div>

              {/* MARKETING VISA INFORMATION */}

            </div>
          )}

          {/* TAB 2: ITEMS REQUESTED - MOBILE RESPONSIVE */}
          {activeTab === "items" && (
            <div className="max-w-8xl mx-auto space-y-6">
              {/* Header Card */}
              <div className="bg-white/70 backdrop-blur-2xl rounded-3xl p-8 border border-white/ shadow-xl">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">Item Details</h3>
                    <p className="text-gray-600">Add and manage items for this purchase order</p>
                  </div>

                  {/* ✅ Import/Export Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={exportTemplate}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold flex items-center gap-2 shadow-lg text-sm"
                    >
                      <Download size={16} />
                      Template
                    </button>

                    <button
                      onClick={exportItems}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold flex items-center gap-2 shadow-lg text-sm"
                    >
                      <Download size={16} />
                      Export
                    </button>

                    <label className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-semibold flex items-center gap-2 shadow-lg cursor-pointer text-sm">
                      <Upload size={16} />
                      Import
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleImportItems}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* DESKTOP TABLE VIEW - Hidden on mobile */}
              <div className="hidden lg:block overflow-x-auto">
                <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200">
                  <div className="grid gap-3 font-semibold text-xs text-slate-600" style={{ gridTemplateColumns: "1.5fr 1.5fr 120px 70px 80px 2fr 70px" }}>
                    <div>Item Code</div>
                    <div>Item Description</div>
                    <div>Specification</div>
                    <div>Qty</div>
                    <div>UOM</div>
                    <div>Remarks/Notes</div>
                    <div>Action</div>
                  </div>
                </div>

                {items.map((item, index) => (
                  <div key={index} className="grid gap-3 mb-3 text-sm" style={{ gridTemplateColumns: "1.5fr 1.5fr 120px 70px 80px 2fr 70px" }}>
                    {/* ✅ ITEM CODE - Hiwalay na input */}
                    <input
                      className="border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={item.itemCode}
                      onChange={(e) => handleItemChange(index, "itemCode", e.target.value)}
                      placeholder="Item Code"
                      disabled
                    />

                    {/* ✅ ITEM DESCRIPTION with Search - Hiwalay na input */}
                    <div className="relative">
                      <input
                        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={item.itemDescription}
                        onChange={(e) => handleItemChange(index, "itemDescription", e.target.value)}
                        placeholder="Description"
                      />
                      <button
                        onClick={() => openItemSearchModal(index)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1.5 transition-all"
                        title="Search items">
                        <Search size={16} />
                      </button>
                    </div>

                    {/* Specification */}
                    <input
                      className="border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={item.material}
                      onChange={(e) => handleItemChange(index, "material", e.target.value)}
                      placeholder="Spec"
                    />

                    {/* Qty */}
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      type="number"
                      value={item.qty}
                      onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                    />

                    {/* UOM */}
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                      type="text"
                      value={item.uom}
                      onChange={(e) => handleItemChange(index, "uom", e.target.value)}
                      placeholder="UOM"
                      disabled
                      readOnly
                      list={`uom-suggestions-${index}`}
                    />


                    {/* Remarks */}
                    <input
                      className="border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={item.remarks}
                      onChange={(e) => handleItemChange(index, "remarks", e.target.value)}
                      placeholder="Add remarks or notes..."
                    />

                    {/* Delete Button */}
                    <button
                      onClick={() => removeItem(index)}
                      className="border border-red-200 rounded-lg px-2 py-2.5 text-red-600 hover:bg-red-50 transition-all flex items-center justify-center"
                      disabled={items.length === 1}>
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>

              {/* MOBILE CARD VIEW - Hidden on desktop */}
              <div className="lg:hidden space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-bold text-slate-700 text-sm">Item #{index + 1}</span>
                      <button
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-all"
                        disabled={items.length === 1}>
                        <X size={18} />
                      </button>
                    </div>

                    <div className="space-y-3 text-sm">
                      {/* ✅ Item Code field for mobile */}
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Item Code</label>
                        <input
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={item.itemCode}
                          onChange={(e) => handleItemChange(index, "itemCode", e.target.value)}
                          placeholder="Item Code"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Item Description</label>
                        <div className="relative">
                          <input
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={item.itemDescription}
                            readOnly
                            onChange={(e) => handleItemChange(index, "itemDescription", e.target.value)}
                          />
                          <button
                            onClick={() => openItemSearchModal(index)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1.5 transition-all"
                            title="Search items">
                            <Search size={16} />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Material/Specification</label>
                        <input
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={item.material}
                          onChange={(e) => handleItemChange(index, "material", e.target.value)}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">Qty</label>
                          <input
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            type="number"
                            value={item.qty}
                            onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">UOM</label>
                          <input
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                            type="text"
                            value={item.uom}
                            onChange={(e) => handleItemChange(index, "uom", e.target.value)}
                            placeholder="UOM"
                            list={`uom-suggestions-mobile-${index}`}
                          />
                          <datalist id={`uom-suggestions-mobile-${index}`}>
                            <option value="PC" />
                            <option value="BOX" />
                            <option value="SET" />
                            <option value="UNIT" />
                            <option value="GA" />
                            <option value="LI" />
                            <option value="PAIL" />
                            <option value="PL" />
                          </datalist>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks/Notes</label>
                        <input
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={item.remarks}
                          onChange={(e) => handleItemChange(index, "remarks", e.target.value)}
                          placeholder="Add remarks or notes..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
      <button
                onClick={addItem}
                className="mt-6 bg-blue-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-all font-semibold shadow-sm text-sm w-full sm:w-auto justify-center">
                <PlusCircle size={18} /> Add Item
              </button>
           
            </div>
          )}
          {/* TAB 3: ATTACHMENTS */}
          {activeTab === "attachments" && (
            <div className="backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6 sm:mb-8">
                <div className="w-1 h-6 sm:h-8 bg-blue-600 rounded-full"></div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800">
                  Attachments
                </h2>
              </div>

              <div className="backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 lg:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 text-sm">
                  <div>
                    <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">Select File</label>
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                        const file = e.dataTransfer.files[0];
                        if (file) {
                          setAttachment(prev => ({ ...prev, file }));
                        }
                      }}
                      className="w-full border-2 border-dashed border-slate-300 rounded-lg p-6 sm:p-8 cursor-pointer bg-white hover:border-blue-400 hover:bg-blue-50/50 transition-all"
                      onClick={() => document.getElementById('fileInput').click()}>
                      <div className="flex flex-col items-center gap-3">
                        <Upload size={32} className="text-slate-400 sm:w-10 sm:h-10" />
                        <p className="text-slate-600 font-semibold text-xs sm:text-sm text-center">Drag & Drop or Click to Upload</p>
                        <p className="text-xs text-slate-500 text-center">PDF, JPG, PNG, DOCX up to 10MB</p>
                      </div>
                    </div>
                    <input
                      id="fileInput"
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.xls,.csv,.zip"
                      onChange={handleFileChange}
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2 text-slate-600 text-xs sm:text-sm">Attachment Description</label>
                    <textarea
                      className="w-full border border-slate-200 rounded-lg p-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                      placeholder="Enter description..."
                      rows="6"
                      value={attachment.description}
                      onChange={(e) => setAttachment(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                </div>

                {attachment.file && (
                  <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm flex-shrink-0">
                        {attachment.file.type.includes('pdf') && (
                          <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                          </svg>
                        )}
                        {attachment.file.type.includes('image') && (
                          <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                          </svg>
                        )}
                        {attachment.file.type.includes('word') && (
                          <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                          </svg>
                        )}
                        {!attachment.file.type.includes('pdf') && !attachment.file.type.includes('image') && !attachment.file.type.includes('word') && (
                          <svg className="w-6 h-6 sm:w-8 sm:h-8 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">{attachment.file.name}</p>
                        <p className="text-xs sm:text-sm text-slate-600">{(attachment.file.size / 1024).toFixed(2)} KB</p>
                      </div>
                      <button
                        onClick={() => setAttachment({ file: null, description: attachment.description })}
                        className="bg-red-100 hover:bg-red-200 text-red-600 p-2 rounded-lg transition-all flex-shrink-0">
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 sm:mt-12">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="w-1 h-6 sm:h-8 bg-green-600 rounded-full"></div>
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-green-700">
                    Submit for Approval
                  </h2>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER WITH NAVIGATION BUTTONS - RESPONSIVE */}
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col gap-4 sm:gap-0 sm:flex-row sm:justify-between sm:items-center">
            {/* Navigation Buttons */}
            <div className="flex gap-2 sm:gap-3 order-2 sm:order-1">
              <button
                onClick={() => {
                  if (activeTab === "items") setActiveTab("info");
                  if (activeTab === "attachments") setActiveTab("items");
                }}
                disabled={activeTab === "info"}
                className={`flex-1 sm:flex-initial px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-sm ${activeTab === "info"
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                  }`}>
                ← Previous
              </button>

              <button
                onClick={() => {
                  if (activeTab === "info") setActiveTab("items");
                  if (activeTab === "items") setActiveTab("attachments");
                }}
                disabled={activeTab === "attachments"}
                className={`flex-1 sm:flex-initial px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-sm ${activeTab === "attachments"
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}>
                Next →
              </button>
            </div>

            {/* Step Indicator */}
            <div className="text-xs sm:text-sm text-slate-600 font-semibold text-center order-1 sm:order-2">
              Step {activeTab === "info" ? "1" : activeTab === "items" ? "2" : "3"} of 3
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 order-3">
              <button
                onClick={() => setShowDraftModal(true)}
                className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gray-500 text-white rounded-lg font-bold hover:bg-gray-600 flex items-center justify-center gap-2 text-sm">
                <FolderOpen size={16} />
                <span className="hidden sm:inline">Load Draft ({savedDrafts.length})</span>
                <span className="sm:hidden">Drafts ({savedDrafts.length})</span>
              </button>
              <button
                onClick={saveDraft}
                className="px-4 sm:px-6 py-2 sm:py-2.5 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 flex items-center justify-center gap-2 text-sm">
                <Save size={16} />
                <span className="hidden sm:inline">Save Draft</span>
                <span className="sm:hidden">Save</span>
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 sm:px-6 py-2 sm:py-2.5 bg-green-600 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 font-semibold disabled:opacity-50 text-sm">
                <Send size={16} />
                <span className="hidden sm:inline">{isSubmitting ? "Submitting..." : "Send to Approvers"}</span>
                <span className="sm:hidden">{isSubmitting ? "Sending..." : "Submit"}</span>
              </button>
            </div>
          </div>
        </div>
        {/* Requestor Search Modal */}
        {showRequestorModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col animate-scale-in">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Search size={24} />
                  <h2 className="text-xl font-bold">Search Requestor</h2>
                </div>
                <button
                  onClick={() => {
                    setShowRequestorModal(false);
                    setRequestorSearchQuery("");
                    setCurrentPage(1); // ✅ Reset page
                  }}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Search Input */}
              <div className="p-6 border-b border-slate-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search by name or department..."
                    value={requestorSearchQuery}
                    onChange={(e) => {
                      handleRequestorSearch(e.target.value);
                      setCurrentPage(1); // ✅ Reset to page 1 on search
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              </div>

              {/* Requestors List */}
              <div className="flex-1 overflow-y-auto p-6">
                {filteredRequestors.length === 0 ? (
                  <div className="text-center py-12">
                    <Search size={48} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 font-semibold">No requestors found</p>
                    <p className="text-slate-400 text-sm">Try a different search term</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* ✅ PAGINATION: Show only current page */}
                    {filteredRequestors
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((req) => (
                        <div
                          key={req.id}
                          onClick={() => {
                            selectRequestor(req);
                            setCurrentPage(1); // ✅ Reset page after selection
                          }}
                          className="p-4 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-slate-800">{req.name}</p>
                              <p className="text-sm text-slate-600">{req.department}</p>
                            </div>
                            <div className="text-blue-600">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* ✅ PAGINATION FOOTER */}
              {filteredRequestors.length > 0 && (
                <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 rounded-b-2xl">
                  <div className="flex items-center justify-between">
                    {/* Results count */}
                    <div className="text-sm text-slate-600">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredRequestors.length)} of {filteredRequestors.length} requestors
                    </div>

                    {/* Pagination controls */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all"
                      >
                        Previous
                      </button>

                      {/* Page numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, Math.ceil(filteredRequestors.length / itemsPerPage)) }, (_, i) => {
                          const totalPages = Math.ceil(filteredRequestors.length / itemsPerPage);
                          let pageNum;

                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-8 h-8 rounded text-sm font-medium transition-all ${currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'border border-slate-300 hover:bg-slate-50'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredRequestors.length / itemsPerPage), p + 1))}
                        disabled={currentPage >= Math.ceil(filteredRequestors.length / itemsPerPage)}
                        className="px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}